import { cookieStorage, createStorage, http } from 'wagmi'
import { bsc, bscTestnet, base, baseSepolia, polygon, polygonAmoy, mainnet, sepolia } from 'wagmi/chains'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import { isTestnet, TESTNET_CHAIN_IDS, MAINNET_CHAIN_IDS } from './contracts-config'

// Reown Project ID from https://cloud.reown.com (formerly WalletConnect)
// Fallback to a known working project ID if env is not set
const envProjectId = process.env.NEXT_PUBLIC_WALLETCONNECT_PROJECT_ID
export const projectId = envProjectId || 'df5941aa1f336c438f869c633c8ae254'

// Debug logging (only in development)
if (typeof window !== 'undefined') {
    console.log('[WagmiConfig] Project ID source:', envProjectId ? 'env' : 'fallback')
    console.log('[WagmiConfig] Project ID:', projectId?.substring(0, 8) + '...')
}

// ============================================
// TESTNET CHAINS
// ============================================
const testnetChains = [bscTestnet, sepolia, polygonAmoy, baseSepolia] as const
// ============================================
// MAINNET CHAINS
// ============================================
const mainnetChains = [bsc, mainnet, polygon, base] as const

// Active chains based on network mode
export const supportedChains = isTestnet ? testnetChains : mainnetChains

// Metadata for Reown AppKit - URL must match Reown dashboard domain settings
const getUrl = () => {
    if (typeof window !== 'undefined') {
        return window.location.origin
    }
    // Use the actual URL/port from environment or default to localhost
    return process.env.NEXT_PUBLIC_APP_URL || 'http://localhost:3711'
}

// Build absolute icon URL
const getIconUrl = () => {
    const baseUrl = getUrl()
    return `${baseUrl}/icon.svg`
}

export const metadata = {
    name: 'TokenKickstarter',
    description: 'Decentralized Token Launchpad',
    url: getUrl(),
    icons: [getIconUrl()],
}

// ============================================
// CHAIN METADATA - TESTNET
// ============================================
const testnetChainMetadata = {
    [bscTestnet.id]: {
        name: 'BSC Testnet',
        shortName: 'BSC-Test',
        nativeCurrency: 'tBNB',
        explorer: process.env.NEXT_PUBLIC_BSC_TESTNET_EXPLORER || 'https://testnet.bscscan.com',
        rpcUrl: process.env.NEXT_PUBLIC_BSC_TESTNET_RPC || 'https://data-seed-prebsc-1-s1.binance.org:8545',
        icon: '/chains/bsc.svg',
    },
    [sepolia.id]: {
        name: 'Sepolia',
        shortName: 'Sepolia',
        nativeCurrency: 'SepoliaETH',
        explorer: process.env.NEXT_PUBLIC_SEPOLIA_EXPLORER || 'https://sepolia.etherscan.io',
        rpcUrl: process.env.NEXT_PUBLIC_SEPOLIA_RPC || 'https://rpc.sepolia.org',
        icon: '/chains/ethereum.svg',
    },
    [polygonAmoy.id]: {
        name: 'Polygon Amoy',
        shortName: 'Amoy',
        nativeCurrency: 'MATIC',
        explorer: process.env.NEXT_PUBLIC_POLYGON_AMOY_EXPLORER || 'https://amoy.polygonscan.com',
        rpcUrl: process.env.NEXT_PUBLIC_POLYGON_AMOY_RPC || 'https://rpc-amoy.polygon.technology',
        icon: '/chains/polygon.svg',
    },
    [baseSepolia.id]: {
        name: 'Base Sepolia',
        shortName: 'Base-Test',
        nativeCurrency: 'ETH',
        explorer: process.env.NEXT_PUBLIC_BASE_SEPOLIA_EXPLORER || 'https://sepolia.basescan.org',
        rpcUrl: process.env.NEXT_PUBLIC_BASE_SEPOLIA_RPC || 'https://sepolia.base.org',
        icon: '/chains/base.svg',
    },
}

// ============================================
// CHAIN METADATA - MAINNET
// ============================================
const mainnetChainMetadata = {
    [bsc.id]: {
        name: 'BNB Smart Chain',
        shortName: 'BSC',
        nativeCurrency: 'BNB',
        explorer: process.env.NEXT_PUBLIC_BSC_EXPLORER || 'https://bscscan.com',
        rpcUrl: process.env.NEXT_PUBLIC_BSC_RPC_URL || 'https://bsc-dataseed1.binance.org',
        icon: '/chains/bsc.svg',
    },
    [mainnet.id]: {
        name: 'Ethereum',
        shortName: 'ETH',
        nativeCurrency: 'ETH',
        explorer: process.env.NEXT_PUBLIC_ETH_EXPLORER || 'https://etherscan.io',
        rpcUrl: process.env.NEXT_PUBLIC_ETHEREUM_RPC_URL || 'https://eth.llamarpc.com',
        icon: '/chains/ethereum.svg',
    },
    [polygon.id]: {
        name: 'Polygon',
        shortName: 'Polygon',
        nativeCurrency: 'MATIC',
        explorer: process.env.NEXT_PUBLIC_POLYGON_EXPLORER || 'https://polygonscan.com',
        rpcUrl: process.env.NEXT_PUBLIC_POLYGON_RPC_URL || 'https://polygon-rpc.com',
        icon: '/chains/polygon.svg',
    },
    [base.id]: {
        name: 'Base',
        shortName: 'Base',
        nativeCurrency: 'ETH',
        explorer: process.env.NEXT_PUBLIC_BASE_EXPLORER || 'https://basescan.org',
        rpcUrl: process.env.NEXT_PUBLIC_BASE_RPC_URL || 'https://mainnet.base.org',
        icon: '/chains/base.svg',
    },
}

// Active chain metadata based on network mode
export const chainMetadata = isTestnet ? testnetChainMetadata : mainnetChainMetadata

// Create transport configuration
const createTransports = () => {
    if (isTestnet) {
        return {
            [bscTestnet.id]: http(testnetChainMetadata[bscTestnet.id].rpcUrl),
            [sepolia.id]: http(testnetChainMetadata[sepolia.id].rpcUrl),
            [polygonAmoy.id]: http(testnetChainMetadata[polygonAmoy.id].rpcUrl),
            [baseSepolia.id]: http(testnetChainMetadata[baseSepolia.id].rpcUrl),
        }
    }
    return {
        [bsc.id]: http(mainnetChainMetadata[bsc.id].rpcUrl),
        [mainnet.id]: http(mainnetChainMetadata[mainnet.id].rpcUrl),
        [polygon.id]: http(mainnetChainMetadata[polygon.id].rpcUrl),
        [base.id]: http(mainnetChainMetadata[base.id].rpcUrl),
    }
}

// Create Wagmi adapter for Reown AppKit
export const wagmiAdapter = new WagmiAdapter({
    storage: createStorage({
        storage: cookieStorage,
    }),
    ssr: true,
    projectId,
    networks: [...supportedChains],
    transports: createTransports() as any,
})

// Export wagmi config for use with hooks
export const wagmiConfig = wagmiAdapter.wagmiConfig

// Helper to get explorer URL
export function getExplorerUrl(chainId: number, hash: string, type: 'tx' | 'address' | 'token' = 'tx') {
    const meta = (chainMetadata as any)[chainId]
    if (!meta) return ''
    return `${meta.explorer}/${type}/${hash}`
}

// Helper to get chain name
export function getChainName(chainId: number) {
    const meta = (chainMetadata as any)[chainId]
    return meta?.name || 'Unknown'
}

// Helper to get native currency
export function getNativeCurrency(chainId: number) {
    const meta = (chainMetadata as any)[chainId]
    return meta?.nativeCurrency || 'ETH'
}

// Export network mode for easy checking
export { isTestnet }
